'use client'

import type { ApplySuccessPageProps } from '@app/p/apply-success/[token]/[step]/page'
import Button, { buttonClassNames } from '@components/primitives/Button'
import { Icon } from '@components/primitives/Icon'
import { classMerge } from '@components/utilities/classMerge'
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons/faArrowRight'
import { faPhoneHangup } from '@fortawesome/pro-regular-svg-icons/faPhoneHangup'
import { faVolume } from '@fortawesome/pro-regular-svg-icons/faVolume'
import { trackEvent } from '@lib/tracking'
import { TrackingEvents } from '@lib/tracking/TrackingEvents'
import { Call, Device } from '@twilio/voice-sdk'
import { useRouter } from 'next/navigation'
import { useCallback, useRef, useState } from 'react'
import { getApplySuccessRouteToRedirectTo } from '../getApplySuccessRouteToRedirectTo'
import ClinicLocationHeader from './ClinicLocationHeader'

const consoleLogger = console

export function PatientCallToSiteInteraction(
  props: ApplySuccessPageProps & {
    conditionName?: string | null
    patientCallToSiteAccessToken?: string | null
    patientName?: string | null
    patientPhoneAlias?: string | null
  },
) {
  const {
    clinicName,
    conditionName,
    idToken,
    inFlowCallingRelayPhoneNumber,
    mostQualifiedTrialApplicationIdToken,
    patientCallToSiteAccessToken,
    patientName,
    patientPhoneAlias,
    step,
    trialLocationImageId,
  } = props

  const router = useRouter()
  const [isCalling, setIsCalling] = useState(false)
  const deviceRef = useRef<Device | null>(null)
  const callRef = useRef<Call | null>(null)
  const advanceToNextStep = useCallback(() => {
    const nextStep = getApplySuccessRouteToRedirectTo(step, props)
    router.push(nextStep)
  }, [router, step])

  const setupDevice = useCallback(async () => {
    if (
      Boolean(deviceRef.current) ||
      !patientCallToSiteAccessToken ||
      !patientPhoneAlias
    ) {
      return
    }

    const device = new Device(patientCallToSiteAccessToken, {
      // Set Opus as our preferred codec. Opus generally performs better, requiring less bandwidth and
      // providing better audio quality in restrained network conditions.
      codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
      logLevel: 1,
    })
    device.on('registered', function () {
      consoleLogger.info('Twilio.Device Ready to make and receive calls!')
    })
    device.on('error', function (error) {
      consoleLogger.error('Twilio.Device Error: ' + error.message)
    })
    deviceRef.current = device
    return device
  }, [patientCallToSiteAccessToken])

  const setupPhoneCallWebClient = useCallback(async () => {
    const device = deviceRef.current ?? (await setupDevice())
    if (!device || !patientPhoneAlias || !inFlowCallingRelayPhoneNumber) {
      return
    }
    consoleLogger.info(
      'Setting up phone call web client with access token %o from %o to %o',
      patientCallToSiteAccessToken,
      patientPhoneAlias,
      inFlowCallingRelayPhoneNumber,
    )
    trackEvent(TrackingEvents.PATIENT_CALL_TO_SITE_CALL_DIALED, {
      mostQualifiedTrialApplicationIdToken,
      patientPhoneAlias,
      trialApplicationIdToken: idToken,
      trialProRelayPhoneNumber: inFlowCallingRelayPhoneNumber,
    })

    // Use the most qualified trial application to make the call - that is what is used to get the trial pro relay phone number and the patient phone alias
    const params = {
      CallerId: patientPhoneAlias,
      To: inFlowCallingRelayPhoneNumber,
      idToken: mostQualifiedTrialApplicationIdToken,
    }

    if (callRef.current) {
      callRef.current.disconnect()
      callRef.current = null
    }

    const call = await device.connect({ params }) // performs the actual dial
    callRef.current = call

    call.on('accept', function (call: Call) {
      consoleLogger.info('Twilio.Call accepted' + JSON.stringify(call))
      trackEvent(TrackingEvents.PATIENT_CALL_TO_SITE_CALL_ACCEPTED, {
        mostQualifiedTrialApplicationIdToken,
        patientPhoneAlias,
        trialApplicationIdToken: idToken,
        trialProRelayPhoneNumber: inFlowCallingRelayPhoneNumber,
      })
    })
    call.on('reject', function (call: Call) {
      consoleLogger.info('Twilio.Call rejected' + JSON.stringify(call))
      trackEvent(TrackingEvents.PATIENT_CALL_TO_SITE_CALL_REJECTED, {
        mostQualifiedTrialApplicationIdToken,
        patientPhoneAlias,
        trialApplicationIdToken: idToken,
        trialProRelayPhoneNumber: inFlowCallingRelayPhoneNumber,
      })
    })
    call.on('cancel', function (call: Call) {
      consoleLogger.info('Twilio.Call cancelled' + JSON.stringify(call))
      trackEvent(TrackingEvents.PATIENT_CALL_TO_SITE_CALL_CANCELED, {
        mostQualifiedTrialApplicationIdToken,
        patientPhoneAlias,
        trialApplicationIdToken: idToken,
        trialProRelayPhoneNumber: inFlowCallingRelayPhoneNumber,
      })
    })
    call.on('disconnect', function (call: Call) {
      consoleLogger.info('Twilio.Call disconnected' + JSON.stringify(call))
      trackEvent(TrackingEvents.PATIENT_CALL_TO_SITE_CALL_END_CLICKED, {
        mostQualifiedTrialApplicationIdToken,
        trialApplicationIdToken: idToken,
        val: 'Call disconnected',
      })

      consoleLogger.info('advanceToNextStep')
      advanceToNextStep()
    })
  }, [advanceToNextStep])

  const endCall = useCallback(() => {
    if (callRef.current) {
      callRef.current.disconnect()
      callRef.current = null
    }
    advanceToNextStep()
  }, [callRef.current])

  const playDtmfTone = (digitString: string) => {
    callRef.current?.sendDigits(digitString)
  }

  return (
    <div className='flex h-full flex-col'>
      <div className='fixed inset-0 -z-10 bg-gradient-to-b from-white via-white to-primary50' />

      <div className='flex max-w-4xl flex-col items-center justify-between gap-16 md:mx-auto md:mt-4 md:flex-row'>
        <div className='flex flex-col gap-4 md:gap-6 '>
          <ClinicLocationHeader
            clinicName={clinicName}
            title={'Calling'}
            trialLocationImageId={trialLocationImageId}
          />
          <h3 className={`text-2xl font-extrabold leading-[130%] md:text-3xl`}>
            {isCalling
              ? "We're connecting you to the clinic, please stay on the line"
              : `The study team is expecting to hear from you. Start the call now!`}
          </h3>

          <div className='flex flex-col gap-4'>
            <div className='flex flex-row items-center gap-4'>
              <div className='flex size-8 flex-none items-center justify-center rounded-md bg-primary50'>
                <Icon className='size-4 text-primary600' icon={faVolume} />
              </div>
              <p className='text-base'>Please make sure your volume is on</p>
            </div>
          </div>

          <div className='flex flex-col items-start gap-6 rounded-md border-2 border-neutral100 p-6'>
            <h4 className='text-base font-bold text-neutral900'>
              What to say when the clinic picks up
            </h4>
            <div className='flex flex-col gap-2'>
              <p className='text-base font-medium leading-[150%] text-neutral600'>
                Hi, my name is {patientName}. I just submitted an application on
                Power for a {conditionName ?? ''} study at your clinic.
              </p>
              <p className='text-base font-medium leading-[150%] text-neutral600'>
                I&apos;m calling to discuss my application to see if I&apos;m a
                candidate for a screening visit.
              </p>
            </div>
          </div>

          <div className='flex w-full flex-row items-center justify-start gap-6'>
            <Button
              className={classMerge(
                buttonClassNames.primary.black,
                isCalling && 'disabled:opacity-75',
                'w-56 rounded-full text-lg leading-[150%]',
              )}
              disabled={isCalling}
              onClick={() => {
                trackEvent(TrackingEvents.PATIENT_CALL_TO_SITE_CALL_STARTED, {
                  mostQualifiedTrialApplicationIdToken,
                  trialApplicationIdToken: idToken,
                })
                setIsCalling(true)
                setupPhoneCallWebClient()
              }}
            >
              {isCalling ? (
                'Calling...'
              ) : (
                <>
                  Start Call
                  <Icon className={'ml-2'} icon={faArrowRight} />
                </>
              )}
            </Button>

            <Button
              className={classMerge(
                buttonClassNames.outline.white,
                !isCalling && 'disabled:opacity-75',
                'w-56 rounded-full text-lg leading-[150%]',
              )}
              disabled={!isCalling}
              onClick={() => {
                trackEvent(
                  TrackingEvents.PATIENT_CALL_TO_SITE_CALL_END_CLICKED,
                  {
                    mostQualifiedTrialApplicationIdToken,
                    trialApplicationIdToken: idToken,
                    val: 'End call button clicked',
                  },
                )
                endCall()
              }}
            >
              <Icon className={'mr-2'} icon={faPhoneHangup} />
              End Call
            </Button>
          </div>
        </div>

        {/* Dialpad */}
        <div className='flex w-[300px] flex-none flex-col items-center gap-3'>
          <div className='flex w-60 flex-col gap-4 rounded-md border-2 border-neutral100 bg-white p-4 drop-shadow-sm'>
            <div className={'mx-auto grid grid-cols-3 gap-2'}>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, '*', 0, '#'].map((digit) => (
                <Button
                  className={classMerge(
                    buttonClassNames.outline.purple,
                    'w-12 rounded-full',
                  )}
                  key={digit}
                  onClick={() => {
                    trackEvent(
                      TrackingEvents.PATIENT_CALL_TO_SITE_DIAL_PAD_PRESSED,
                      {
                        mostQualifiedTrialApplicationIdToken,
                        trialApplicationIdToken: idToken,
                        val: digit.toString(),
                      },
                    )
                    playDtmfTone(digit.toString())
                  }}
                >
                  {digit}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
